import React, { useMemo } from "react";
import NuDate from "../../../../../Components/Utils/NuDate";
import capitalizeFirstLetter from "../../../../../Components/Utils/Funcs";
import { GetMultiRole, GetUserDetail } from "../../../../../Components/Utils/roles";
import FilesView from "../../../../../Components/Utils/FilesView";

const ALLOWED_ROLES_FOR_ASSIGN = [
  "admin",
  "super admin",
  "management",
  "manager",
  "coordinator",
  "sales co-ordinator",
  "proc co-ordinator",
];

const ALLOWED_ROLES_FOR_ATTN = [
  "admin",
  "super admin",
  "management",
  "manager",
  "coordinator",
];

const PrDetailsCard = ({ data, filePathList = [] }) => {
  const { name = "" } = GetUserDetail();

  const canSeeAllAssignees = useMemo(
    () => GetMultiRole(ALLOWED_ROLES_FOR_ASSIGN),
    []
  );

  const canSeeAttn = useMemo(
    () => GetMultiRole(ALLOWED_ROLES_FOR_ATTN),
    []
  );

  const assignedNamesDisplay = useMemo(() => {
    if (!data?.rfqAssigneeNames || data.rfqAssigneeNames.length === 0) {
      return "No Data";
    }
    if (canSeeAllAssignees) {
      return data.rfqAssigneeNames.join(", ");
    }
    return (
      data.rfqAssigneeNames.find(
        (i) => i.toLowerCase() === name.toLowerCase()
      ) || "None"
    );
  }, [data?.rfqAssigneeNames, canSeeAllAssignees, name]);

  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 flex flex-col gap-3">
      {/* Top Banner: Reference, Description, Metadata & Status */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between border-b border-gray-100 pb-3">
        {/* Left Section */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col gap-1 md:flex-row md:items-start">
            <p className="shrink-0 text-sm font-semibold text-[#111537] md:w-32">
              {data?.ref || "N/A"}
            </p>
            <span className="hidden md:block text-[#111537]">:</span>
            <p className="flex-1 text-sm text-[#606279] break-words">
              {data?.description || "None"}
            </p>
          </div>

          <div className="mt-2 flex flex-col gap-1 text-xs text-[#111537] sm:flex-row sm:flex-wrap sm:gap-4">
            <p>
              <span className="font-medium">Date:</span>{" "}
              {data?.date ? (
                <NuDate value={data.date} format="DD-MMM-YYYY, hh:mm A" />
              ) : (
                "N/A"
              )}
            </p>
            <p>
              <span className="font-medium">Created By:</span>{" "}
              {data?.preparedByName
                ? capitalizeFirstLetter(data.preparedByName)
                : "None"}
            </p>
          </div>
        </div>

        {/* Right Section: Status Badge */}
        <div className="flex justify-start md:justify-end">
          <div className="inline-flex overflow-hidden rounded-md border border-[#579BB1] text-xs">
            <span className="bg-white px-3 py-1 font-medium text-black">
              Status
            </span>
            <span className="bg-[#579BB1] px-3 py-1 font-medium text-white">
              {data?.status || "None"}
            </span>
          </div>
        </div>
      </div>

      {/* Grid Section: Key-Value Details */}
      <div className="w-full flex flex-wrap px-2 border rounded-lg bg-gray-50/50">
        <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-x-8">
          <div className="w-full flex px-2 border-b sm:border-b-0 border-gray-100">
            <p className="w-1/3 py-2 text-xs font-medium text-[#111537]">
              RFQ Assign.
            </p>
            <p className="w-[65%] py-2 text-xs pl-5 text-[#606279]">
              : {assignedNamesDisplay}
            </p>
          </div>

          <div className="w-full flex px-2 border-b sm:border-b-0 border-gray-100">
            <p className="w-1/3 py-2 text-xs font-medium text-[#111537]">
              Assign. Date
            </p>
            <p className="w-[65%] py-2 text-xs pl-5 text-[#606279]">
              :{" "}
              {data?.date ? (
                <NuDate value={data?.date} format="DD-MMM-YYYY" />
              ) : (
                "N/A"
              )}
            </p>
          </div>

          <div className="w-full flex px-2 border-b sm:border-b-0 border-gray-100">
            <p className="w-1/3 py-2 text-xs font-medium text-[#111537]">
              Deadline
            </p>
            <p className="w-[65%] py-2 text-xs pl-5 text-[#606279]">
              :{" "}
              {data?.deadLine ? (
                <NuDate value={data?.deadLine} format="DD-MMM-YYYY" />
              ) : (
                "N/A"
              )}
            </p>
          </div>

          <div className="w-full flex px-2 border-b sm:border-b-0 border-gray-100">
            <p className="w-1/3 py-2 text-xs font-medium text-[#111537]">
              Sales Person
            </p>
            <p className="w-[65%] py-2 text-xs pl-5 text-[#606279]">
              : {data?.salesPersonName || "N/A"}
            </p>
          </div>

          <div className="w-full flex px-2 border-b sm:border-b-0 border-gray-100">
            <p className="w-1/3 py-2 text-xs font-medium text-[#111537]">
              Days Elapsed
            </p>
            <p className="w-[65%] py-2 text-xs pl-5 text-[#606279]">
              : {data?.daysElapsed ?? 0}
            </p>
          </div>

          {canSeeAttn && (
            <div className="w-full flex px-2 border-b sm:border-b-0 border-gray-100">
              <p className="w-1/3 py-2 text-xs font-medium text-[#111537]">
                Attn.
              </p>
              <p className="w-[65%] py-2 text-xs pl-5 text-[#606279]">
                : {data?.attn || "N/A"}
              </p>
            </div>
          )}

          {data?.reviewedByName && (
            <div className="w-full flex px-2 border-b sm:border-b-0 border-gray-100">
              <p className="w-1/3 py-2 text-xs font-medium text-[#111537]">
                Reviewed By
              </p>
              <p className="w-[65%] py-2 text-xs pl-5 text-[#606279]">
                : {data.reviewedByName}
              </p>
            </div>
          )}

          {data?.verifiedByName && (
            <div className="w-full flex px-2 border-b sm:border-b-0 border-gray-100">
              <p className="w-1/3 py-2 text-xs font-medium text-[#111537]">
                Verified By
              </p>
              <p className="w-[65%] py-2 text-xs pl-5 text-[#606279]">
                : {data.verifiedByName}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Notes & Attachments Cards */}
      <div className="flex flex-col sm:flex-row gap-4 mt-2">
        <div className="w-full sm:w-[50%]">
          <p className="text-xs font-medium text-[#111537] p-1">
            Notes from Customer
          </p>
          <div className="border h-28 overflow-y-auto rounded-lg bg-white p-2">
            <p className="text-xs break-words text-[#606279]">
              {data?.notesFromCustomer || "-"}
            </p>
          </div>
        </div>

        <div className="w-full sm:w-[50%]">
          <p className="p-1 text-xs font-medium text-[#111537]">
            YPR Attachments
          </p>
          <div className="border overflow-y-auto h-28 rounded-lg p-2 bg-white">
            <div className="w-full flex gap-2 flex-wrap">
              {filePathList && filePathList.length > 0 ? (
                filePathList.map((fileItem, idx) => (
                  <FilesView fileView={fileItem} key={idx} />
                ))
              ) : (
                <p className="text-xs text-[#9DA3AA] p-1">No attachments available</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(PrDetailsCard);
